import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import useApi from '../utils/api';


const GameSpaceContext = createContext();
const STORAGE_KEY = 'gameSpace.selectedSpaceId';

export const useGameSpace = () => useContext(GameSpaceContext);

const getStoredSpaceId = () => {
  try {
    const id = localStorage.getItem(STORAGE_KEY);
    if (id == null || id === '') return null;
    const num = Number(id);
    return Number.isFinite(num) ? num : null;
  } catch {
    return null;
  }
};

export const GameSpaceProvider = ({ children }) => {
  const { organizationId, userRole, refreshGamespaceRole, isAuthenticated, isLoading } = useAuth();
  const { get, post, put, del } = useApi();
  const [gameSpaces, setGameSpaces] = useState([]);
  const [selectedSpaceId, setSelectedSpaceIdState] = useState(getStoredSpaceId);
  const [isLoadingSpaces, setIsLoadingSpaces] = useState(false);
  const audience = process.env.REACT_APP_AUDIENCE;
  const {loginWithRedirect} = useAuth(); // Check if the user is authenticated

  const setSelectedSpaceId = (id) => {
    setSelectedSpaceIdState(id);
    try {
      if (id != null) localStorage.setItem(STORAGE_KEY, String(id));
      else localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
  };
  

  const fetchAvailableSpaces = useCallback(async () => {
    if (!organizationId || !userRole) return;
    setIsLoadingSpaces(true);
    try {
      // Available Learnspaces only (excludes soft-deleted). Org-owner admin list uses GET /gamespaces separately.
      const spaces = await get(`${process.env.REACT_APP_API_URL}/api/user/gamespaces`);
      if (Array.isArray(spaces)) {
        setGameSpaces(spaces);
        if (spaces.length > 0) {
          const currentId = getStoredSpaceId();
          const idInList = currentId != null && spaces.some(s => s.space_id === currentId);
          if (!idInList) {
            setSelectedSpaceId(spaces[0].space_id);
          }
        } else {
          setSelectedSpaceId(null);
        }
      }
    } catch (error) {
      console.error('Failed to fetch game spaces', error);
    } finally {
      setIsLoadingSpaces(false);
    }
  }, [organizationId, userRole, get]);

  useEffect(() => {
    fetchAvailableSpaces();
  }, [fetchAvailableSpaces]);

  // --- Access grants state ---
  const [accessGrants, setAccessGrants] = useState([]);
  const [hasSpaceWideAccess, setHasSpaceWideAccess] = useState(false);
  const [hasGrants, setHasGrants] = useState(false);
  const [grantsLoading, setGrantsLoading] = useState(false);

  const canAccessGame = useCallback((gameId) => {
    if (hasSpaceWideAccess) return true;
    return accessGrants.some(g => g.game_id === gameId);
  }, [accessGrants, hasSpaceWideAccess]);

  // Refresh gamespace-specific role when space selection changes
  useEffect(() => {
    if (selectedSpaceId && isAuthenticated && !isLoading) {
      refreshGamespaceRole(selectedSpaceId);
    }
    console.log('Selected space ID changed in GameSpaceContext to:', selectedSpaceId);
    // Intentionally omit refreshGamespaceRole to avoid effect churn from function identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpaceId, isAuthenticated, isLoading]);

  // Fetch access grants when selected space changes.
  // Gate on auth readiness to avoid an initial unauthenticated request that
  // returns 401 before Auth0 finishes loading (selectedSpaceId is hydrated
  // synchronously from localStorage, so this effect would otherwise fire too early).
  useEffect(() => {
    if (!selectedSpaceId) {
      setAccessGrants([]);
      setHasSpaceWideAccess(false);
      setHasGrants(false);
      return;
    }
    if (!isAuthenticated || isLoading) {
      return;
    }

    let isMounted = true;
    setGrantsLoading(true);

    const fetchGrants = async () => {
      try {
        const data = await get(
          `${process.env.REACT_APP_API_URL}/api/gamespaces/${selectedSpaceId}/access-grants/me`,
          selectedSpaceId
        );
        if (isMounted) {
          setAccessGrants(data.grants || []);
          setHasSpaceWideAccess(data.has_space_wide_access || false);
          setHasGrants(data.has_grants || false);
        }
      } catch (err) {
        console.error('Failed to fetch access grants', err);
        if (isMounted) {
          setAccessGrants([]);
          setHasSpaceWideAccess(false);
          setHasGrants(false);
        }
      } finally {
        if (isMounted) setGrantsLoading(false);
      }
    };

    fetchGrants();
    return () => { isMounted = false; };
  }, [selectedSpaceId, isAuthenticated, isLoading, get]);

  useEffect(() => {
    const retrySpaceName = sessionStorage.getItem("retry:createGameSpace");

    if (retrySpaceName) {
      sessionStorage.removeItem("retry:createGameSpace");
      createGameSpace(retrySpaceName);
    }
  }, []);

  const createGameSpace = async (spaceName) => {
    if (!organizationId) return;
    try {
      let response = await post(`${process.env.REACT_APP_API_URL}/api/gamespaces`, { space_name: spaceName });

      if (response && response.gamespace) {
        const newSpace = response.gamespace;
        setGameSpaces(prev => [...prev, newSpace]);
        setSelectedSpaceId(newSpace.space_id);
      }
    } catch (error) {
      console.error('Failed to create game space', error);
    }
  };

  const updateGameSpaceName = async (spaceId, newName) => {
    try {
      const response = await put(`${process.env.REACT_APP_API_URL}/api/gamespaces/${spaceId}`, { space_name: newName });
      if (response && response.gamespace) {
        setGameSpaces(prev => prev.map(gs =>
          gs.space_id === spaceId ? { ...gs, space_name: newName } : gs
        ));
      }
    } catch (error) {
      console.error('Failed to rename game space', error);
    }
  };

  const transferGameSpaceOwnership = async (spaceId, newOwnerPublicUserId) => {
    try {
      const response = await put(`${process.env.REACT_APP_API_URL}/api/gamespaces/${spaceId}/transfer-ownership`, {
        new_owner_public_user_id: newOwnerPublicUserId
      });
      if (response && response.gamespace) {
        setGameSpaces(prev => prev.map(gs =>
          gs.space_id === spaceId ? { ...gs, space_owner_user_id: newOwnerPublicUserId } : gs
        ));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to transfer ownership', error);
      return false;
    }
  };

  const archiveGameSpace = async (spaceId) => {
    try {
      const response = await del(`${process.env.REACT_APP_API_URL}/api/gamespaces/${spaceId}`);
      if (response?.error) {
        console.error('Failed to archive game space', response);
        return false;
      }
      setGameSpaces((prev) => {
        const next = prev.filter((gs) => gs.space_id !== spaceId);
        if (selectedSpaceId === spaceId) {
          setSelectedSpaceId(next.length > 0 ? next[0].space_id : null);
        }
        return next;
      });
      return true;
    } catch (error) {
      console.error('Failed to archive game space', error);
      return false;
    }
  };

  const restoreGameSpace = async (spaceId) => {
    try {
      const response = await put(`${process.env.REACT_APP_API_URL}/api/gamespaces/${spaceId}/restore`, {});
      if (response?.error) {
        console.error('Failed to restore game space', response);
        return false;
      }
      await fetchAvailableSpaces();
      return true;
    } catch (error) {
      console.error('Failed to restore game space', error);
      return false;
    }
  };

  const value = {
    gameSpaces,
    selectedSpaceId,
    setSelectedSpaceId,
    isLoadingSpaces,
    createGameSpace,
    updateGameSpaceName,
    transferGameSpaceOwnership,
    archiveGameSpace,
    restoreGameSpace,
    refreshAvailableSpaces: fetchAvailableSpaces,
    accessGrants,
    hasSpaceWideAccess,
    hasGrants,
    grantsLoading,
    canAccessGame,
  };

  return <GameSpaceContext.Provider value={value}>{children}</GameSpaceContext.Provider>;
};

